import { motion } from "framer-motion";
import { ArrowUpRight, BarChart3, CalendarDays, Clock, Table2 } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const POSTS = [
    {
        id: "spreadsheets",
        cat: "Operations",
        date: "Oct 02, 2026",
        title: "The Hidden Cost of Running Your Business on Spreadsheets",
        excerpt:
            "Spreadsheets seem simple, but disconnected information, duplicate work and missed updates can quietly hold a growing business back.",
        read: "8 min read",
        icon: Table2,
        color: "#00E5FF",
        shot: "Blog image — laptop running spreadsheets on a desk",
    },
    {
        id: "ai-small-business",
        cat: "AI & Automation",
        date: "Sep 24, 2026",
        title: "What Can AI Actually Do for a Small Business?",
        excerpt:
            "From smarter scheduling to automated follow-ups and better reporting, here are practical ways AI can improve day-to-day operations.",
        read: "7 min read",
        icon: BarChart3,
        color: "#0066FF",
        shot: "Blog image — laptop showing the TedOps dashboard",
    },
    {
        id: "scheduling",
        cat: "Scheduling",
        date: "Sep 18, 2026",
        title: "Still Scheduling Jobs with Calls, Texts and WhatsApp?",
        excerpt:
            "Manual coordination creates delays, double bookings and wasted time. See how connected scheduling helps teams stay in sync.",
        read: "6 min read",
        icon: CalendarDays,
        color: "#A855F7",
        shot: "Blog image — engineer with phone and laptop by a TedOps van",
    },
];

const PostImage = ({ post }) => {
    const Icon = post.icon;
    return (
        <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-t-3xl bg-gradient-to-br from-[#0B1220] via-[#101A30] to-[#0B1220]">
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{ background: `radial-gradient(circle at 70% 20%, ${post.color}1A 0%, transparent 55%)` }}
            />
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
                <Icon className="h-6 w-6" style={{ color: post.color }} strokeWidth={1.75} />
            </div>
            <p className="absolute bottom-3 px-4 text-center font-mono-brand text-[9px] uppercase tracking-[0.2em] text-[#475569]">
                Temporary placeholder · {post.shot}
            </p>
            <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#0F172A] shadow-sm">
                {post.cat}
            </span>
        </div>
    );
};

const PostCard = ({ post, index }) => (
    <motion.article
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: EASE, delay: index * 0.1 }}
        className="group flex cursor-pointer flex-col rounded-3xl border border-[#0F172A]/[0.07] bg-white shadow-[0_2px_12px_-4px_rgba(11,18,32,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(11,18,32,0.2)]"
        data-testid={`blog-card-${index + 1}`}
    >
        <div className="overflow-hidden rounded-t-3xl">
            <div className="transition-transform duration-500 group-hover:scale-[1.03]">
                <PostImage post={post} />
            </div>
        </div>
        <div className="relative z-10 -mt-8 flex flex-1 flex-col rounded-t-3xl bg-white px-7 pb-6 pt-6 shadow-[0_-8px_20px_-12px_rgba(11,18,32,0.15)]">
            <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">{post.date}</p>
            <h3 className="mt-3 font-display text-xl font-bold leading-snug text-[#0F172A] transition-colors duration-300 group-hover:text-[#0066FF]">
                {post.title}
            </h3>
            <p className="mt-3 flex-1 text-[15px] leading-relaxed text-[#64748B]">{post.excerpt}</p>
            <div className="mt-6 flex items-center justify-between border-t border-[#0F172A]/[0.07] pt-5">
                <span className="flex items-center gap-1.5 text-xs font-medium text-[#64748B]">
                    <Clock className="h-3.5 w-3.5" />
                    {post.read}
                </span>
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0B1220] text-white transition-colors duration-300 group-hover:bg-gradient-to-br group-hover:from-[#0066FF] group-hover:to-[#A855F7]">
                    <ArrowUpRight className="h-4 w-4" />
                </span>
            </div>
        </div>
    </motion.article>
);

export default function Blog() {
    return (
        <section
            className="relative bg-white"
            style={{
                backgroundImage:
                    "linear-gradient(to right, rgba(15,23,42,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.035) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
            }}
            data-testid="blog-section"
            aria-labelledby="blog-heading"
        >
            <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <span
                            className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/10 bg-white px-4 py-1.5 shadow-sm"
                            data-testid="blog-eyebrow"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#0066FF]" />
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#475569] sm:text-xs">
                                Latest from the Blog
                            </span>
                        </span>
                        <h2
                            id="blog-heading"
                            className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#0F172A] sm:text-5xl"
                            data-testid="blog-heading"
                        >
                            Work smarter.
                            <br />
                            <span className="text-brand-gradient">Stay ahead.</span>
                        </h2>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
                        className="flex max-w-md flex-col items-start gap-6"
                    >
                        <p className="text-base leading-relaxed text-[#64748B]" data-testid="blog-intro">
                            Practical insights on AI, operations, automation and the everyday
                            challenges of running a growing service business.
                        </p>
                        <button
                            type="button"
                            className="group inline-flex items-center gap-3 rounded-full bg-[#0B1220] py-2 pl-6 pr-2 text-xs font-bold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.03]"
                            data-testid="view-articles-button"
                        >
                            View All Articles
                            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-transform duration-300 group-hover:rotate-45">
                                <ArrowUpRight className="h-4 w-4" />
                            </span>
                        </button>
                    </motion.div>
                </div>

                <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
                    {POSTS.map((post, i) => (
                        <PostCard key={post.id} post={post} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
